/**
 * NoteTile / NoteMini — the two sizes of "anonymous note" cards used
 * across Home, Ratings, and the Landing sample row.
 *
 * NoteTile  — larger, used in 4-up grids on Landing.
 * NoteMini  — smaller, used in 2×2 grids in Home's "Other notes" rail.
 */
import { Icons } from './Icons';

export type NoteData = {
  id: string;
  yes: boolean;
  comment: string;
  when: string;   // human-readable, e.g. "2h ago" or "today"
};

export function NoteTile({ r, onShare }: { r: NoteData; onShare?: () => void }) {
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 18, padding: 20, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <VerdictDot yes={r.yes} size={30} />
        <div
          className="mono"
          style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}
        >
          {r.yes ? 'Would date' : "Wouldn't"} · {r.when}
        </div>
        {onShare && (
          <button
            onClick={onShare}
            aria-label="Share note"
            style={{
              marginLeft: 'auto',
              width: 30,
              height: 30,
              borderRadius: 30,
              background: 'var(--chip)',
              color: 'var(--ink-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.share size={13} />
          </button>
        )}
      </div>
      <div className="serif" style={{ fontSize: 17, lineHeight: 1.45 }}>
        “{r.comment}”
      </div>
    </div>
  );
}

export function NoteMini({ r, onShare }: { r: NoteData; onShare?: () => void }) {
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <VerdictDot yes={r.yes} size={24} />
        <div
          className="mono"
          style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}
        >
          {r.yes ? 'Yes' : 'No'} · {r.when}
        </div>
        {onShare && (
          <button
            onClick={onShare}
            aria-label="Share note"
            style={{
              marginLeft: 'auto',
              width: 24,
              height: 24,
              borderRadius: 24,
              background: 'var(--chip)',
              color: 'var(--ink-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.share size={11} />
          </button>
        )}
      </div>
      <div
        className="serif"
        style={{
          fontSize: 14,
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        “{r.comment}”
      </div>
    </div>
  );
}

function VerdictDot({ yes, size }: { yes: boolean; size: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size,
        background: yes ? 'var(--coral)' : 'var(--ink)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {yes ? <Icons.heart size={size * 0.5} fill="currentColor" /> : <Icons.x size={size * 0.5} stroke={2.4} />}
    </div>
  );
}
